import { nanoid } from "nanoid";
import { ReactElement } from "react";
import { Route, Routes } from "react-router-dom";
import Component404 from "../components/Component404/Component404";
import { useAppSelector } from "../store/reduxHooks";
import { SELECT_PROFILE_ROLE } from "../store/selectors/profileSelectors";
import { SELECT_ROUTINIG } from "../store/selectors/routingSelector";
import { USER } from "../types/indexTypeEnum";
import NotAuthorizeRoute from "../utils/HOC/NonAuthorizeRoute";
import PrivateRoute from "../utils/HOC/PrivateRoute";
import {
  PageApplication,
  PageApplicationAdditional,
  PageApplicationCreate,
  PageApplications,
  PageBanks,
  PageListAgent,
  PageListClient,
  PageListClientForAgent,
  PageListManager,
  PageMainRoute,
  PageProfile,
  PageReport,
  PageUnsubscribe,
  PageUserCreate,
} from "../views/indexPage";
import { PageAgent } from "../views/pageAgent/PageAgent";
import { PageAgreementDataProcessingDocument } from "../views/pageAgreementDocument/PageAgreementDataProcessingDocument";
import { PageClient } from "../views/pageClient/PageClient";
import { PageDocumentsSigning } from "../views/pageDocumentsSigning/PageDocumentsSigning";
import { PageLogin } from "../views/pageLogin/PageLogin";
import { PageManager } from "../views/pageManager/PageManager";
import { PagePrivacyPolicyDocument } from "../views/pagePrivacyPolicyDocument/pagePrivacyPolicyDocument";
import { PageRecoveryPassword } from "../views/pageRecoveryPassword/pageRecoveryPassword";
import { PageRegisterAgent } from "../views/pageRegisterAgent/pageRegisterAgent";
import { PageRegisterClient } from "../views/pageRegisterClient/pageRegisterClient";
import { PageRequiringSigningDocuments } from "../views/pageRequiringSigningDocuments/pageRequiringSigningDocuments";
import { PageListAgentIndividual } from "../views/PageListAgentIndividual/PageListAgentIndividual";
import { PageAgentIndividual } from "../views/pageAgentIndividual/PageAgentIndividual";
import { PageSelectRegistration } from "../views/pageSelectRegistration/pageSelectRegistration";

interface IPages {
  [key: string]: ReactElement;
}

const NotAuthorizeRoutes = [
  {
    element: (
      <NotAuthorizeRoute>
        <PageLogin />
      </NotAuthorizeRoute>
    ),
    path: "/login",
  },
  {
    element: (
      <NotAuthorizeRoute>
        <PageRegisterAgent />
      </NotAuthorizeRoute>
    ),
    path: "/registerAgent",
  },
  {
    element: (
      <NotAuthorizeRoute>
        <PageRegisterClient />
      </NotAuthorizeRoute>
    ),
    path: "/registerClient",
  },
  {
    element: (
      <NotAuthorizeRoute>
        <PageRecoveryPassword />
      </NotAuthorizeRoute>
    ),
    path: "/recoveryPassword",
  },
  {
    element: (
      <NotAuthorizeRoute>
        <PageSelectRegistration />
      </NotAuthorizeRoute>
    ),
    path: "/selectRegistration",
  },
  {
    element: <PageClient />,
    path: "/client/:id",
  },
  {
    element: <PageManager />,
    path: "/manager/:id",
  },
  {
    element: <PageAgent />,
    path: "/agent/:id",
  },
];

// маршруты
export function AppRoutes(): ReactElement {
  const role = useAppSelector(SELECT_PROFILE_ROLE);
  const routingSlice = useAppSelector(SELECT_ROUTINIG);

  const ROUTS_ELEMENT: IPages = {
    PageApplications: <PageApplications />,
    PageBanks: <PageBanks />,
    PageListClient:
      role === USER.AGENT_SIMPLE ? (
        <PageListClientForAgent />
      ) : (
        <PageListClient />
      ),
    PageListAgent: <PageListAgent />,
    PageReport: <PageReport />,
    PageProfile: <PageProfile />,
    ApplicationCreate: <PageApplicationCreate />,
    Application: <PageApplication />,
    PageListManager: <PageListManager />,
    PageUserCreate: <PageUserCreate />,
    PageClient: <PageClient />,
    PageAgent: <PageAgent />,
  };

  return (
    <>
      <Routes>
        <Route
          path={"/documents-signing"}
          element={
            <PrivateRoute>
              <PageDocumentsSigning />
            </PrivateRoute>
          }
        />

        <Route path={"/"} element={<PageMainRoute routes={routingSlice} />}>
          <Route
            path={"/client/:id"}
            element={
              <PrivateRoute>
                <PageClient />
              </PrivateRoute>
            }
          />

          <Route
            path={"/agent/:id"}
            element={
              <PrivateRoute>
                <PageAgent />
              </PrivateRoute>
            }
          />
          <Route
            path={"/manager/:id"}
            element={
              <PrivateRoute>
                <PageManager />
              </PrivateRoute>
            }
          />

          <Route
            path="/list-agent-individual"
            element={
              <PrivateRoute>
                <PageListAgentIndividual />
              </PrivateRoute>
            }
          />

          <Route
            path="/agent-individual"
            element={
              <PrivateRoute>
                <PageAgentIndividual />
              </PrivateRoute>
            }
          />

          <Route
            path={
              "/system-bank/create-data-to-bank/:applicationId/application-bank/:applicationBankId"
            }
            element={
              <PrivateRoute>
                <PageApplicationAdditional />
              </PrivateRoute>
            }
          />

          <Route
            path="/application-create"
            element={
              <PrivateRoute>
                <PageApplicationCreate />
              </PrivateRoute>
            }
          />
          {routingSlice?.map((routeGroup) => {
            const { routes } = routeGroup;
            return routes?.map((route) => {
              const element = ROUTS_ELEMENT[route.element];
              const ID = nanoid();
              const link = route.to;
              if (link === "application") {
                return (
                  <Route key={ID} path={route.to} element={element}>
                    <Route
                      key={ID + "application"}
                      path={":id"}
                      element={
                        <PrivateRoute>
                          <PageApplication />
                        </PrivateRoute>
                      }
                    />
                  </Route>
                );
              }
              return (
                <Route
                  key={ID}
                  path={"/" + route.to}
                  element={<PrivateRoute>{element}</PrivateRoute>}
                />
              );
            });
          })}

          <Route
            path="/requiringDocs"
            element={<PageRequiringSigningDocuments />}
          />
        </Route>
        <Route
          path="*"
          element={
            <PrivateRoute>
              <PageMainRoute routes={routingSlice} />
            </PrivateRoute>
          }
        >
          <Route
            path="*"
            element={
              <PrivateRoute>
                <Component404 />
              </PrivateRoute>
            }
          />
        </Route>

        {NotAuthorizeRoutes.map(({ path, element }) => (
          <Route key={path} path={path} element={element} />
        ))}

        <Route
          path="/agreementDataProcessing"
          element={<PageAgreementDataProcessingDocument />}
        />
        <Route
          path="/agreementPrivacyPolicy"
          element={<PagePrivacyPolicyDocument />}
        />
        <Route path="/unsubscribe/:token" element={<PageUnsubscribe />} />
      </Routes>
    </>
  );
}
